import { Component, ViewChild, ViewChildren, ViewContainerRef, QueryList, Output, EventEmitter, OnDestroy, OnInit, AfterViewInit, Input, OnChanges, SimpleChanges, Injector } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatRow } from '@angular/material/table';
import { KEService } from '../ke.service';
import { KEResource } from '../ke';
import { Subscription, Observable } from 'rxjs';
import { FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { FormStatus } from 'src/app/services/forms/form-status';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { BaseComponent } from 'src/app/common/base/base.component';
import { KEnableUpdate } from '../store/ke/kenable.action';

@Component({
  selector: 'ke-side-panel',
  templateUrl: './ke-side-panel.component.html',
  styleUrls: ['./ke-side-panel.component.scss']
})
export class KESidePanelComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

  @Input() filter!: string;

  public displayedColumns: string[] = ['serialNo', 'shutterName', 'typeName', 'statusName'];
  public dataSource = new MatTableDataSource<KEResource>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChildren('matrow', { read: ViewContainerRef }) rows!: QueryList<ViewContainerRef>;

  @ViewChildren('matrow')
  datarows!: QueryList<MatRow>;

  public serialNo!: string;
  public selectedRowIndex = -1;
  public selectedIndex = -1;
  public showClosed = false;

  public allowNew?: boolean;
  public disabled = false;
  maxHeight!: string;

  @Output() rowSelected = new EventEmitter<KEResource>();
  @Output() newDocument = new EventEmitter<KEResource>();
  @Output() toggleSidePanel = new EventEmitter<any>();
  @Output() discard = new EventEmitter<any>();

  ke?: KEResource | null;
  kes!: KEResource[];
  kes$!: Observable<KEResource[]>;
  kesSubs!: Subscription;

  formStatuses!: FormStatus[];
  formStatuses$!: Observable<FormStatus[]>;
  formStatusesSubs!: Subscription;

  constructor(
    protected override injector: Injector,
    private _ke: KEService,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.applyFilter(this.filter);
  }

  override ngOnDestroy(): void {
    this.kesSubs?.unsubscribe();
    this.formStatusesSubs?.unsubscribe();
  }

  ngOnInit() {
    this.loadFormStatuses();
    this.allowNew = (this.getCurrentUser() && this.hasPrivilege(PrivilegeEnum.CreateKEChecklist)) ?? false;
    this.loadKEs();
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  loadFormStatuses() {
    this.formStatuses$ = this.store.select(state => state.FormStatuses.data);
    this.formStatusesSubs = this.formStatuses$.subscribe(data => {
      if (data) {
        this.formStatuses = data;
      }
    });
  }

  //#region Load KE

  /**
   * Loads KE data from the store and initializes the data table source.
   * Applies sorting, filters based on status and display preferences, and restores previously opened documents.
   */
  loadKEs() {
    // Select and subscribe to KE data from store
    this.kes$ = this.store.select(state => state.KEnables.data);
    this.kesSubs = this.kes$.subscribe(async data => {
      if (data.length) {
        // Sort and filter data based on closed status visibility
        this.kes = data.sort((a, b) => this.utils.sort(a.serialNo, b.serialNo, false));
        // Reapply the active search term when the selected KE refreshes the store data.
        this.applyFilter(this.filter ?? '');

        // Restore previously opened document if available
        const openDocument = await this.checkOpenDocument<KEResource>(FormType.KE, data);
        if (openDocument && (openDocument?.document.id != this.ke?.id || !this.ke?.wfTable)) {
          this.showClosed = openDocument.closed;
          this.getValues(openDocument.document);
        }
      }
    });
  }

  async selectRow(row?: KEResource) {
    if (await this.canDeactivate()) {
      if (row) row.wfTable = undefined;
      this.getValues(row);
    }
  }

  /**
   * Selects a KE resource row, fetches its latest data, and emits the selection event.
   * Updates the store if the fetched data differs from the current row data.
   * @param row - The selected KE resource row (optional)
   */
  getValues(row?: KEResource) {
    // Set current KE and emit selection
    this.ke = row;
    this.rowSelected.emit(row);
    this.selectedRowIndex = row?.id ?? -1;
    this.setOpenDocument(FormType.KE, row?.id);
    // Fetch and sync latest data if row exists
    if (row) {
      this._ke.getById(row.id).subscribe(ke => {
        if (ke && JSON.stringify(ke) !== JSON.stringify(row)) {
          row = ke;
          this.ke = ke;
          this.store.dispatch(new KEnableUpdate(ke.id, ke));
        }
        this.rowSelected.emit(row);
      });
    }
    else {
      // Emit empty selection if no row provided
      this.rowSelected.emit(row);
    }
  }

  /**
   * Determines if the component can deactivate based on form dirty state.
   * Emits a discard event if the form is dirty and deactivation is allowed.
   * @returns {Promise<boolean>} true if deactivation is allowed, false otherwise
   */
  override async canDeactivate(): Promise<boolean> {
    // Check if form has unsaved changes
    if (this.isFormDirty()) {
      // Call parent deactivation logic
      const value = await super.canDeactivate();
      // Emit discard event if deactivation is approved
      if (value)
        this.discard.emit(true);
      return value;
    }
    else return true;
  }

  create() {
    this.selectedRowIndex = -1;
    this.serialNo = '{Serial No}';
    const newDoc: KEResource =
    {
      id: 0,
      type: FormType.KE,
      serialNo: this.serialNo,
      status: 0,
      statusName: 'New'
    };
    this.newDocument.emit(newDoc);
  }

  unselect() {
    this.ke = null;
    this.selectedIndex = -1;
  }

  //#region FILTERS

  /**
   * Filters the KE data source by status and text matching across multiple fields.
   * Applies case-insensitive search against serialNo, shutterName, typeName, and statusName.
   * @param filterValue - The text to filter by
   */
  applyFilter(filterValue?: string) {
    // Normalize filter to lowercase for case-insensitive matching
    const val = filterValue?.toLowerCase() ?? '';
    // Apply status filter and text search across multiple fields
    this.dataSource = new MatTableDataSource(this.kes?.filter(x => this.showClosed ? true : (x.status != FormStatusEnum.Closed && x.status != FormStatusEnum.Canceled))?.filter(k => k.serialNo.toLowerCase().includes(val) || k.shutterName?.toLowerCase().includes(val) || k.typeName?.toLowerCase().includes(val) || k.statusName?.toLowerCase().includes(val)));
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  /**
   * Reloads KE data after the closed filter changes.
   */
  changeClosed() {
    this.loadKEs();
  }

  //#region TableScroll
  /**
 * Ensures the row at the given index is visible within the provided viewport height.
 * Scrolls the row into view only when it is outside the visible bounds.
 * @param index - Row index to locate in the rendered list
 * @param height - Current viewport height used to determine visibility
 */
  showElement(index: number, height: number) {
    const indexstr = index.toString();
    const row = this.rows.find(row => row.element.nativeElement.getAttribute('position') === indexstr);
    if (row != null) {
      const rect = row.element.nativeElement.getBoundingClientRect();
      if ((rect.y <= 0) || ((rect.y + rect.height) > height)) {
        row.element.nativeElement.scrollIntoView(false, { behavior: 'instant' });
      }
      return;
    }
    console.log('not found');
  }

  getPosition(row: any): number {
    const i = this.dataSource.data.findIndex(r => {
      return r.id == row.id;
    });
    return i;
  }

  getColor(row: KEResource) {
    return this.formStatuses?.find(x => x.id == row.status)?.color;
  }
}
